/**
 * Cars subsystem — CPU-simulated vehicles that drive on the road mask.
 *
 * Generation/render decomposition mirrors trees:
 *   - This module reads back the road mask at startup, spawns `carInstanceCount`
 *     cars at drivable texels, owns the GPU instance buffer, and rewrites it each
 *     frame via `device.queue.writeBuffer`.
 *   - `src/render/cars.ts` consumes the same instance buffer with stepMode='instance'
 *     and draws indirect against a fixed count.
 *
 * Instance struct (16 bytes):
 *   offset 0..11   pos      vec3<f32>
 *   offset 12..15  heading  f32   (radians around +Y; +X at heading=0)
 *
 * Steering: field-following on the rasterized road mask. Each frame the car steps
 * forward and samples mask at its lookahead point. If that drops below threshold
 * — or every `carReevaluatePeriodFrames` (per-car jittered) — it probes 8 bearings
 * and picks the one with highest `mask × forward-alignment`. Off-road for too
 * long → respawn at a random drivable texel.
 */

import { config } from '../config.ts';
import { createStorageBuffer } from '../gpu/buffers.ts';
import { mulberry32, fnv1a, type Rng } from '../util/prng.ts';

export interface CarsModule {
  /** Per-instance vertex buffer — pos (vec3) + heading (f32). Stride = 16. */
  readonly instanceBuffer: GPUBuffer;
  /** Total instances (fixed; matches `config.carInstanceCount`). */
  readonly maxInstances: number;
  /** Per-frame simulation step + buffer upload. */
  update(dt: number): void;
  destroy(): void;
}

export interface CarsModuleOptions {
  seed: number;
  /** Bilinear height sampler from `createTerrainSampler` — keeps cars on terrain. */
  heightSampler: (worldX: number, worldZ: number) => number;
}

const INSTANCE_STRIDE = 16;

export async function createCars(
  device: GPUDevice,
  roadMaskTexture: GPUTexture,
  opts: CarsModuleOptions,
): Promise<CarsModule> {
  const maskWidth = roadMaskTexture.width;
  const maskHeight = roadMaskTexture.height;
  const mask = await readbackRoadMask(device, roadMaskTexture);

  const halfWorld = config.worldSize * 0.5;

  const uvToWorld = (texelX: number, texelY: number): [number, number] => {
    // Match the convention in terrain-sampler/terrain.wgsl: u = (worldX + half) / worldSize.
    const u = (texelX + 0.5) / maskWidth;
    const v = (texelY + 0.5) / maskHeight;
    return [u * config.worldSize - halfWorld, v * config.worldSize - halfWorld];
  };

  const sampleRoad = (worldX: number, worldZ: number): number => {
    const u = (worldX + halfWorld) / config.worldSize;
    const v = (worldZ + halfWorld) / config.worldSize;
    if (u < 0 || u > 1 || v < 0 || v > 1) return 0;
    const tx = Math.min(maskWidth - 1, Math.max(0, Math.floor(u * maskWidth)));
    const ty = Math.min(maskHeight - 1, Math.max(0, Math.floor(v * maskHeight)));
    return mask[ty * maskWidth + tx] ?? 0;
  };

  // Build the drivable-texel index list once. Cars spawn from this, and the
  // respawn path picks fresh entries when a car gets stuck or escapes.
  const drivableIndices: number[] = [];
  const threshold = config.carRoadThreshold;
  for (let i = 0; i < mask.length; i++) {
    if ((mask[i] ?? 0) >= threshold) drivableIndices.push(i);
  }
  if (drivableIndices.length === 0) {
    // No roads detected — nothing to do; create an empty buffer so render is a no-op.
    const empty = createStorageBuffer(device, INSTANCE_STRIDE, {
      label: 'cars.instances',
      usageExtra: GPUBufferUsage.VERTEX,
    });
    return {
      instanceBuffer: empty,
      maxInstances: 0,
      update() {},
      destroy() { empty.destroy(); },
    };
  }

  const rng: Rng = mulberry32(opts.seed ^ fnv1a('cars'));

  const N = config.carInstanceCount;
  const posX = new Float32Array(N);
  const posY = new Float32Array(N);
  const posZ = new Float32Array(N);
  const heading = new Float32Array(N);
  const speed = new Float32Array(N);
  const offRoadFrames = new Uint16Array(N);
  const reevalAt = new Uint16Array(N);
  const reevalPeriod = new Uint16Array(N);
  const frameCounter = new Uint16Array(1);

  const PROBE_DIRS = 8;
  const PROBE_NOISE = 0.06;

  // Probe 8 bearings; return the radians of the best continuation.
  const probeBestHeading = (x: number, z: number, currentH: number, noiseRng: Rng): number => {
    let bestAngle = currentH;
    let bestScore = -Infinity;
    const lookahead = config.carLookaheadWu;
    for (let i = 0; i < PROBE_DIRS; i++) {
      const a = (i / PROBE_DIRS) * Math.PI * 2;
      const dx = Math.cos(a);
      const dz = Math.sin(a);
      const sample = sampleRoad(x + dx * lookahead, z + dz * lookahead);
      // Forward-alignment factor in [0..1]; rewards continuing roughly forward.
      const align = 0.5 + 0.5 * (Math.cos(a - currentH));
      const noise = (noiseRng() - 0.5) * PROBE_NOISE;
      const score = sample * (0.4 + 0.6 * align) + noise;
      if (score > bestScore) {
        bestScore = score;
        bestAngle = a;
      }
    }
    return bestAngle;
  };

  const spawnCar = (i: number): void => {
    const idx = drivableIndices[Math.floor(rng() * drivableIndices.length)] ?? drivableIndices[0]!;
    const tx = idx % maskWidth;
    const ty = (idx / maskWidth) | 0;
    const [wx, wz] = uvToWorld(tx, ty);
    posX[i] = wx;
    posZ[i] = wz;
    // pos.y is the ground line — the render/shadow shaders lift the box so its
    // base sits on inPos.y, so adding a half-height here would float the car.
    posY[i] = opts.heightSampler(wx, wz);
    heading[i] = probeBestHeading(wx, wz, rng() * Math.PI * 2, rng);
    speed[i] = config.carSpeedMin + rng() * (config.carSpeedMax - config.carSpeedMin);
    offRoadFrames[i] = 0;
    reevalPeriod[i] = Math.max(8, Math.floor(config.carReevaluatePeriodFrames * (0.7 + rng() * 0.6)));
    reevalAt[i] = Math.floor(rng() * reevalPeriod[i]);
  };

  for (let i = 0; i < N; i++) spawnCar(i);

  // Per-frame scratch is laid out for direct upload as the GPU instance buffer.
  const scratch = new Float32Array(N * 4);
  const instanceBuffer = createStorageBuffer(device, N * INSTANCE_STRIDE, {
    label: 'cars.instances',
    usageExtra: GPUBufferUsage.VERTEX,
  });

  const update = (dt: number): void => {
    frameCounter[0] = ((frameCounter[0] ?? 0) + 1) & 0xffff;
    const frame = frameCounter[0] ?? 0;

    for (let i = 0; i < N; i++) {
      let h = heading[i]!;
      const s = speed[i]!;
      const cosH = Math.cos(h);
      const sinH = Math.sin(h);

      // 1. Advance.
      let x = posX[i]! + cosH * s * dt;
      let z = posZ[i]! + sinH * s * dt;

      // 2. Lookahead road sample.
      const lookX = x + cosH * config.carLookaheadWu;
      const lookZ = z + sinH * config.carLookaheadWu;
      const lookSample = sampleRoad(lookX, lookZ);

      // 3. Re-steer if road ahead is gone, or it's time to reconsider (intersections).
      const periodicReeval = ((frame - reevalAt[i]!) & 0xffff) % reevalPeriod[i]! === 0;
      if (lookSample < config.carRoadThreshold || periodicReeval) {
        // Per-car noise rng — cheap fresh stream so all cars don't pick the same bearing.
        const noiseRng = mulberry32(((i + 1) * 0x9e3779b1) ^ (frame * 0x85ebca6b));
        h = probeBestHeading(x, z, h, noiseRng);
      }

      // 4. Track off-road state.
      const here = sampleRoad(x, z);
      if (here < config.carRoadThreshold) {
        offRoadFrames[i] = (offRoadFrames[i]! + 1) & 0xffff;
      } else {
        offRoadFrames[i] = 0;
      }

      // 5. Respawn if stuck off-road or out of world bounds.
      if (
        offRoadFrames[i]! > config.carRespawnAfterOffRoadFrames ||
        x < -halfWorld || x > halfWorld || z < -halfWorld || z > halfWorld
      ) {
        spawnCar(i);
        x = posX[i]!;
        z = posZ[i]!;
        h = heading[i]!;
      } else {
        posX[i] = x;
        posZ[i] = z;
        posY[i] = opts.heightSampler(x, z);
        heading[i] = h;
      }

      // 6. Pack into the GPU upload buffer.
      const base = i * 4;
      scratch[base] = posX[i]!;
      scratch[base + 1] = posY[i]!;
      scratch[base + 2] = posZ[i]!;
      scratch[base + 3] = heading[i]!;
    }

    device.queue.writeBuffer(instanceBuffer, 0, scratch.buffer, scratch.byteOffset, scratch.byteLength);
  };

  // Prime the buffer once so the first frame already has valid contents even if
  // `update(dt)` hasn't been called yet (defensive — main.ts calls update before render).
  for (let i = 0; i < N; i++) {
    const base = i * 4;
    scratch[base] = posX[i]!;
    scratch[base + 1] = posY[i]!;
    scratch[base + 2] = posZ[i]!;
    scratch[base + 3] = heading[i]!;
  }
  device.queue.writeBuffer(instanceBuffer, 0, scratch.buffer, scratch.byteOffset, scratch.byteLength);

  return {
    instanceBuffer,
    maxInstances: N,
    update,
    destroy() {
      instanceBuffer.destroy();
    },
  };
}

async function readbackRoadMask(device: GPUDevice, tex: GPUTexture): Promise<Float32Array> {
  const w = tex.width;
  const h = tex.height;
  const bpr = Math.ceil((w * 4) / 256) * 256;
  const rb = device.createBuffer({
    label: 'cars.roadmask.readback',
    size: bpr * h,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
  const enc = device.createCommandEncoder({ label: 'cars.roadmask.encoder' });
  enc.copyTextureToBuffer(
    { texture: tex },
    { buffer: rb, bytesPerRow: bpr },
    { width: w, height: h, depthOrArrayLayers: 1 },
  );
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const raw = new Float32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  rb.destroy();

  const stride = bpr / 4;
  const data = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      data[y * w + x] = raw[y * stride + x] ?? 0;
    }
  }
  return data;
}
